import { cn } from '../cn';
import type { TipTapVariable, TipTapDocument } from '../Input2/TipTapInput';
import {
  CampaignWaitForConnectPure,
  CampaignWaitForConnectYesBanner,
  type WaitForConnectBranchPreview,
} from '../CampaignDayCard/CampaignWaitForConnectPure';
import { CampaignDayCardPure } from '../CampaignDayCard/CampaignDayCardPure';
import { CampaignNewDayPickerPure } from '../CampaignDayCard/CampaignNewDayPickerPure';
import { isStepDifferentFromTemplate } from '../CampaignDayCard/templateDiff';
import type { CampaignDayCardStep } from '../CampaignDayCard/stepDisplayInfo';
import type { ReminderAction } from '../ManualSelect/ManualSelectPure';
import type { StepHistoryOption, StepResolution } from '../CampaignDayCard/stepResolution';
import type { PreviousEmailThread } from '../ContactDetail/previousEmailThread';
import {
  createStepDataFromEditor,
  daysToMs,
  updateStepDataFromEditor,
  type EditStepPure,
  type CreateStepData,
} from './CGAddStepPure';
import type { CadenceGraphNodesPure, CadenceStepNodePure, CadenceStepTypePure, StepUpdateInputPure } from './types';
import type { SiftConfig, StepData, StepType } from './CTSContentEditingPure';
import { findCadenceThreadSubject, findCadenceThreadSubjectForNewStep } from './previousEmailStep';

/**
 * Walks the cadence graph from `startId` to find the FIRST non-delay
 * step on that branch, and returns a short preview describing it.
 * Returns `undefined` when the branch terminates without ever reaching
 * a real step ("end of cadence").
 *
 * Includes any leading delay days in the label so the preview reads
 * like "Send message in 2 days" rather than just "Send message" when
 * there's a meaningful wait first.
 */
function buildBranchPreview(
  nodes: CadenceGraphNodesPure,
  startId: string | null | undefined,
): WaitForConnectBranchPreview | undefined {
  if (startId == null) return undefined;
  let cursor: CadenceStepNodePure | undefined = nodes[startId];
  let totalDelayMs = 0;
  // Hard cap to defend against pathological cycles in malformed graphs.
  for (let i = 0; i < 50 && cursor != null; i++) {
    if (cursor.type === 'delay') {
      totalDelayMs += cursor.delay;
      cursor = cursor.next.one ? nodes[cursor.next.one] : undefined;
      continue;
    }
    if (cursor.type === 'wait_for_connect') {
      // Nested decision point — surface as a generic label instead of
      // recursing into another fork.
      return { type: cursor.type, label: 'Another decision point' };
    }
    const days = Math.round(totalDelayMs / (24 * 60 * 60 * 1000));
    const verb = STEP_VERB[cursor.type] ?? cursor.type;
    const label = days > 0 ? `${verb} in ${days} ${days === 1 ? 'day' : 'days'}` : verb;
    return { type: cursor.type, label };
  }
  return undefined;
}

const STEP_VERB: Partial<Record<NonNullable<CadenceStepNodePure['type']>, string>> = {
  visit: 'Visit profile',
  follow: 'Follow profile',
  likeRecentPosts: 'Like recent posts',
  connect: 'Send connection request',
  message: 'Send message',
  inmail: 'Send InMail',
  email: 'Send email',
  reminder: 'Manual reminder',
  call: 'Call',
  voice_note: 'Send voice note',
  video_note: 'Send video note',
  sift: 'Filter contacts',
};

// ============================================================================
// Types
// ============================================================================

export type CGWaitForStepPureProps = {
  first?: boolean;
  nodeId: string;
  nodes: CadenceGraphNodesPure;
  msUpToNow: number;
  hasSalesNav?: boolean | null;
  alreadyConnectedToContact?: boolean;
  /**
   * URL to the cadence template page (e.g. `/templates/{templateId}`).
   * Forwarded to the synthetic No-branch `CampaignDayCardPure` so its
   * header surfaces the "See in template" / "Not in template" link.
   */
  templateHref?: string;
  editingStep?: EditStepPure;
  setEditingStep: (value: EditStepPure | undefined) => void;
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
  contactId?: string;
  onChangeType: (nodeId: string, type: string) => void;
  onDeleteNode: (nodeId: string) => void;
  onCreateStep: (data: CreateStepData) => unknown;
  onUpdateStep: (stepId: string, data: StepUpdateInputPure) => unknown;
  resolveStep: (nodeId: string, resolution: StepResolution) => void;
  /** History rows the finish / skip popup offers to link (see CampaignDayCardPure). */
  stepHistoryOptions?: StepHistoryOption[];
  /**
   * The cadence owner's latest email thread with this contact (`getPreviousEmailThread` scoped to
   * the cadence's user), or null. Email step editors thread onto it — see `findCadenceThreadSubject`.
   */
  cadenceEmailThread?: PreviousEmailThread | null;
  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => React.ReactNode;
  reminderActions?: ReminderAction[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;
  // Render prop for recursive CGStep rendering
  renderCGStep: (props: {
    nodeId: string;
    first?: boolean;
    msUpToNow: number;
  }) => React.ReactNode;
  /** When true, the call step type is available (team voice-calling flag). */
  voiceCallingEnabled?: boolean;
  /** Injected dialer for an action-required `call` step — forwarded to the timeline row. */
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => React.ReactNode;
  /** Completes a `call` step handled outside the dialer — see `CallStepActionsPure`. */
  onMarkCallStepDone?: (args: { cadenceStepId: string; outcome: string; notes?: string; callbackAt?: number }) => Promise<void> | void;
  /**
   * Display-only mode — hides the delay edit pill and the empty-branch
   * add-step triggers. See CGStepPureProps.readOnly.
   */
  readOnly?: boolean;
};

// ============================================================================
// Component
// ============================================================================

export function CGWaitForStepPure({
  nodeId,
  nodes,
  msUpToNow,
  alreadyConnectedToContact,
  templateHref,
  editingStep,
  onCreateStep,
  onUpdateStep,
  onDeleteNode,
  resolveStep,
  stepHistoryOptions,
  cadenceEmailThread = null,
  setEditingStep,
  variables,
  documents,
  renderCGStep,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  voiceCallingEnabled,
  renderCadenceCallButton,
  onMarkCallStepDone,
  readOnly,
}: CGWaitForStepPureProps) {
  const node = nodes[nodeId];
  if (node == null) return null;

  // Once the wait_for_connect has been resolved at runtime, the
  // `selectedOption` field tells us which branch fired. Skip rendering
  // the decision card and just show the chosen subtree.
  //
  // - Yes (option='one'): the chosen branch IS the main cadence
  //   timeline continuation — recurse via `renderCGStep` so the
  //   downstream day cards render through their normal CGDelayStepPure
  //   path (gives the new card chrome automatically).
  // - No (option='two'): the chosen branch typically starts with a
  //   leaf step (no leading delay). Routing through `renderCGStep`
  //   would land in the legacy CGLeafNodePure (purple-pill), so use
  //   `renderLeafChainAsDayCard` instead — it collects the leading leaf
  //   chain into a synthetic CampaignDayCardPure with the new chrome.
  // A resolved wait_for_connect whose selected (No) branch is EMPTY must
  // render nothing — mirroring the resolved Yes-empty case below, which
  // already falls through to `return null`. Rendering the bare
  // `renderLeafChainAsDayCard(startId=null)` trigger here duplicated the
  // host's start-of-cadence "add step on a new day" trigger, producing the
  // two stacked add buttons reported in POS-2327.
  if (node.selectedOption === 'two' && node.next.two != null) {
    return (
      <div className="mx-2">
        {renderLeafChainAsDayCard({
          nodes,
          startId: node.next.two,
          parentStepId: node.id,
          msUpToNow: msUpToNow + node.delay,
          editingStep,
          setEditingStep,
          variables,
          documents,
          renderCGStep,
          renderSiftConditionBuilder,
          reminderActions,
          reminderActionsLoading,
          onCreateReminder,
          reminderCreating,
          onReminderError,
          alreadyConnectedToContact,
          templateHref,
          voiceCallingEnabled,
          renderCadenceCallButton,
          onMarkCallStepDone,
          readOnly,
          onCreateStep,
          onUpdateStep,
          onDeleteStep: onDeleteNode,
          resolveStep,
          stepHistoryOptions,
          cadenceEmailThread,
        })}
      </div>
    );
  }
  const selectedNodeId = node.selectedOption ? node.next[node.selectedOption] : undefined;
  if (selectedNodeId) {
    const selectedNode = nodes[selectedNodeId];
    const nodeType = selectedNode?.type;
    // Resolved Yes branch (option='one'): if it starts with a bare leaf
    // step, renderCGStep would land in legacy CGLeafNodePure (purple
    // pill). Route through renderLeafChainAsDayCard (branch-agnostic — it
    // collects leading leaves into a synthetic CampaignDayCardPure with
    // the new chrome). The 'two' resolved case is handled by the early
    // return above.
    const isLeadingLeaf =
      selectedNode != null && nodeType !== 'delay' && nodeType !== 'wait_for_connect';
    if (isLeadingLeaf) {
      return (
        <div className="mx-2">
          {renderLeafChainAsDayCard({
            nodes,
            startId: selectedNodeId,
            parentStepId: node.id,
            msUpToNow,
            editingStep,
            setEditingStep,
            variables,
            documents,
            renderCGStep,
            renderSiftConditionBuilder,
            reminderActions,
            reminderActionsLoading,
            onCreateReminder,
            reminderCreating,
            onReminderError,
            alreadyConnectedToContact,
            templateHref,
            voiceCallingEnabled,
            renderCadenceCallButton,
            onMarkCallStepDone,
            readOnly,
            onCreateStep,
            onUpdateStep,
            onDeleteStep: onDeleteNode,
            resolveStep,
            stepHistoryOptions,
            cadenceEmailThread,
          })}
        </div>
      );
    }
    return (
      <div className={cn(nodeType !== 'delay' && 'mx-1')}>
        {renderCGStep({
          nodeId: selectedNodeId,
          msUpToNow,
        })}
      </div>
    );
  }
  if (node.selectedOption != null) {
    return null;
  }

  const yesPreview = buildBranchPreview(nodes, node.next.one);

  return (
    <>
      {node.state !== 'finished' && (
        <>
          {/* Centered spine bridging the previous day card (which always
              ends with the connect step that triggered this
              wait_for_connect) down to this card — without it the two
              cards float disconnected from each other. */}
          <span
            aria-hidden
            data-testid="cg-wait-for-connect-spine"
            className="mx-auto block h-3 w-px bg-lynch-200 dark:bg-slate-700"
          />
          <div className="mx-2">
            <CampaignWaitForConnectPure
              state={node.state}
              delay={node.delay}
              yesPreview={yesPreview}
              onChangeDelay={readOnly ? undefined : (delayMs) => onUpdateStep(nodeId, { delay: delayMs })}
              noBranchChildren={renderLeafChainAsDayCard({
                nodes,
                startId: node.next.two,
                parentStepId: node.id,
                msUpToNow: msUpToNow + node.delay,
                editingStep,
                setEditingStep,
                variables,
                documents,
                renderCGStep,
                renderSiftConditionBuilder,
                reminderActions,
                reminderActionsLoading,
                onCreateReminder,
                reminderCreating,
                onReminderError,
                alreadyConnectedToContact,
                templateHref,
                voiceCallingEnabled,
                renderCadenceCallButton,
                onMarkCallStepDone,
                readOnly,
                onCreateStep,
                onUpdateStep,
                onDeleteStep: onDeleteNode,
          resolveStep,
          stepHistoryOptions,
          cadenceEmailThread,
              })}
            />
          </div>
          {/* "If accepted" banner labels the cards below as the Yes
              continuation, making the conditional fork unambiguous.
              Always shown so the user knows where the Yes path lives,
              even when it's empty (in which case the BranchAdd below
              surfaces the trigger to add the first Yes-path step). */}
          <div className="mx-2">
            <CampaignWaitForConnectYesBanner />
          </div>
          {/* Empty Yes branch — surface a trigger so the user can add
              the first Yes-path step without scrolling past the fork.
              `option='one'` routes the new step to the Yes branch.
              Centered spine bridges the banner to the trigger so the
              column reads as one continuous flow. No trailing spine
              after the trigger because it's the last element. */}
          {!readOnly && !node.next.one && (
            <>
              <span
                aria-hidden
                className="mx-auto block h-3 w-px bg-lynch-200 dark:bg-slate-700"
              />
              <div className="mx-2">
                <BranchAdd
                  afterStepId={node.id}
                  branchOption="one"
                  previousEmailSubject={findCadenceThreadSubjectForNewStep(
                    nodes,
                    { afterStepId: node.id },
                    cadenceEmailThread,
                  )}
                  editingStep={editingStep}
                  setEditingStep={setEditingStep}
                  variables={variables}
                  documents={documents}
                  renderSiftConditionBuilder={renderSiftConditionBuilder}
                  reminderActions={reminderActions}
                  reminderActionsLoading={reminderActionsLoading}
                  onCreateReminder={onCreateReminder}
                  reminderCreating={reminderCreating}
                  onReminderError={onReminderError}
                  alreadyConnectedToContact={alreadyConnectedToContact}
                  voiceCallingEnabled={voiceCallingEnabled}
                  onCreateStep={onCreateStep}
                />
              </div>
            </>
          )}
        </>
      )}

      {/* Yes branch render. If the first Yes node is a leading delay
          (or wait_for_connect), recurse via `renderCGStep` — that path
          already produces the new day-card chrome through
          CGDelayStepPure. If it's a bare leaf step (e.g. follow,
          message), routing through `renderCGStep` would land in the
          legacy `CGLeafNodePure` (purple-pill) renderer. Use
          `renderLeafChainAsDayCard` instead — despite the name it's
          branch-agnostic: it collects leading leaf chains into a
          synthetic CampaignDayCardPure with the new chrome, and works
          equally well for the Yes path. */}
      {node.next.one &&
        (() => {
          const firstYes = nodes[node.next.one];
          const isLeadingLeaf =
            firstYes != null &&
            firstYes.type !== 'delay' &&
            firstYes.type !== 'wait_for_connect';
          if (!isLeadingLeaf) {
            return renderCGStep({ nodeId: node.next.one, msUpToNow });
          }
          return renderLeafChainAsDayCard({
            nodes,
            startId: node.next.one,
            parentStepId: node.id,
            msUpToNow,
            editingStep,
            setEditingStep,
            variables,
            documents,
            renderCGStep,
            renderSiftConditionBuilder,
            reminderActions,
            reminderActionsLoading,
            onCreateReminder,
            reminderCreating,
            onReminderError,
            alreadyConnectedToContact,
            templateHref,
            voiceCallingEnabled,
            renderCadenceCallButton,
            onMarkCallStepDone,
            readOnly,
            onCreateStep,
            onUpdateStep,
            onDeleteStep: onDeleteNode,
            resolveStep,
            stepHistoryOptions,
            cadenceEmailThread,
          });
        })()}
    </>
  );
}

/**
 * Renders a leading-leaf chain through the new CampaignDayCard chrome
 * instead of the legacy `CGLeafNodePure` purple-pill. Branch-agnostic:
 * used for the wait_for_connect No branch, the wait_for_connect Yes
 * branch (resolved + unresolved) when it starts with a leaf, and the
 * cadence root itself when the very first node is a leaf.
 *
 *   1. Walk forward from `startId`, collecting consecutive non-delay
 *      step ids into a "first day" group.
 *   2. Render those as a `CampaignDayCardPure`.
 *   3. Hand any trailing delay back to the caller's recursive
 *      `renderCGStep` so subsequent days render as normal day cards.
 *
 * When `startId` is null (empty branch), surfaces the new full-width
 * "+ add step on a new day" trigger from `CampaignNewDayPickerPure`,
 * anchored at `parentStepId`.
 */
export function renderLeafChainAsDayCard(opts: {
  nodes: CadenceGraphNodesPure;
  startId: string | null | undefined;
  parentStepId: string;
  msUpToNow: number;
  editingStep: EditStepPure | undefined;
  setEditingStep: (value: EditStepPure | undefined) => void;
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
  renderCGStep: (props: { nodeId: string; first?: boolean; msUpToNow: number }) => React.ReactNode;
  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => React.ReactNode;
  reminderActions?: ReminderAction[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;
  alreadyConnectedToContact?: boolean;
  templateHref?: string;
  voiceCallingEnabled?: boolean;
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => React.ReactNode;
  onMarkCallStepDone?: (args: { cadenceStepId: string; outcome: string; notes?: string; callbackAt?: number }) => Promise<void> | void;
  /** Display-only mode — see CGStepPureProps.readOnly. */
  readOnly?: boolean;
  onCreateStep: (data: CreateStepData) => unknown;
  onUpdateStep: (stepId: string, data: StepUpdateInputPure) => unknown;
  onDeleteStep: (stepId: string) => void;
  resolveStep: (stepId: string, resolution: StepResolution) => void;
  /** History rows the finish / skip popup offers to link (see CampaignDayCardPure). */
  stepHistoryOptions?: StepHistoryOption[];
  /**
   * The cadence owner's latest email thread with this contact (`getPreviousEmailThread` scoped to
   * the cadence's user), or null. Email step editors thread onto it — see `findCadenceThreadSubject`.
   */
  cadenceEmailThread?: PreviousEmailThread | null;
}): React.ReactNode {
  const {
    nodes,
    startId,
    parentStepId,
    msUpToNow,
    editingStep,
    setEditingStep,
    variables,
    documents,
    renderCGStep,
    renderSiftConditionBuilder,
    reminderActions,
    reminderActionsLoading,
    onCreateReminder,
    reminderCreating,
    onReminderError,
    alreadyConnectedToContact,
    templateHref,
    voiceCallingEnabled,
    renderCadenceCallButton,
    onMarkCallStepDone,
    readOnly,
    onCreateStep,
    onUpdateStep,
    onDeleteStep,
    resolveStep,
    stepHistoryOptions,
    cadenceEmailThread = null,
  } = opts;

  // Empty No branch — render the new add-step trigger so the user
  // gets the same chrome as the rest of the cadence. Read-only hosts
  // get nothing: there's no step to show and no way to add one.
  if (startId == null) {
    if (readOnly) return null;
    return (
      <BranchAdd
        afterStepId={parentStepId}
        previousEmailSubject={findCadenceThreadSubjectForNewStep(
          nodes,
          { afterStepId: parentStepId },
          cadenceEmailThread,
        )}
        // option='two' so the new step lands in the No (not accepted)
        // branch — without this the server defaults to 'one' (Yes).
        branchOption="two"
        // Narrow well — shrink picker option buttons so they fit.
        pickerOptionSize="sm"
        // No leading delay on the No branch: the wait_for_connect step
        // itself already owns the delay ("not accepted after N days").
        // Suppresses the redundant "AFTER WAITING N DAYS" caption AND
        // sends `delay: 0` so the new step fires immediately when the
        // timeout trips.
        defaultDelayDays={0}
        editingStep={editingStep}
        setEditingStep={setEditingStep}
        variables={variables}
        documents={documents}
        renderSiftConditionBuilder={renderSiftConditionBuilder ?? (() => null)}
        reminderActions={reminderActions}
        reminderActionsLoading={reminderActionsLoading}
        onCreateReminder={onCreateReminder ?? (async () => {})}
        reminderCreating={reminderCreating}
        onReminderError={onReminderError ?? (() => {})}
        alreadyConnectedToContact={alreadyConnectedToContact}
        voiceCallingEnabled={voiceCallingEnabled}
        onCreateStep={onCreateStep}
      />
    );
  }

  // If the first step is itself a delay, recurse — that path
  // already produces the new day cards.
  const first = nodes[startId];
  if (first == null || first.type === 'delay') {
    return renderCGStep({ nodeId: startId, msUpToNow });
  }

  // Collect a leading run of leaf steps until we hit a delay /
  // wait_for_connect / end. They form one synthetic "first day"
  // for the No branch.
  const leafIds: string[] = [];
  let cursor: CadenceStepNodePure | undefined = first;
  for (let i = 0; i < 50 && cursor != null; i++) {
    if (cursor.type === 'delay' || cursor.type === 'wait_for_connect') break;
    // Mirror CGDelayStepPure's filter: hide resolved (finished/skipped)
    // AND will-never-run (canceled/contact_unavailable) leaves.
    if (
      cursor.state !== 'finished' &&
      cursor.state !== 'skipped' &&
      cursor.state !== 'canceled' &&
      cursor.state !== 'contact_unavailable'
    ) {
      leafIds.push(cursor.id);
    }
    cursor = cursor.next.one ? nodes[cursor.next.one] : undefined;
  }
  const tailId = cursor?.id ?? null;
  const lastLeafId = leafIds[leafIds.length - 1] ?? parentStepId;

  const steps: CampaignDayCardStep[] = leafIds
    .map((id) => nodes[id])
    .filter((n): n is NonNullable<typeof n> => n != null)
    .map((n) => ({
      id: n.id,
      type: n.type,
      state: n.state,
      body2: n.body2,
      subject: n.subject,
      postsToLike: n.postsToLike,
      cc: n.cc,
      bcc: n.bcc,
      siftConditions: n.siftConditions,
      onlyOpenLink: n.onlyOpenLink,
      replyToPrevious: n.replyToPrevious,
      previousEmailSubject:
        n.type === 'email' ? findCadenceThreadSubject(nodes, n.id, cadenceEmailThread) : null,
      isDifferentFromTemplate: isStepDifferentFromTemplate(n),
      error:
        n.errorMessage ??
        n.computedError?.default ??
        n.computedError?.subject ??
        n.computedError?.body2 ??
        null,
      skipReason: n.skipReason,
    }));

  const isPickingHere =
    editingStep?.type === 'add' &&
    editingStep.afterStepId === lastLeafId &&
    editingStep.intent !== 'new-day' &&
    editingStep.data == null;
  const ADD_TYPE_INLINE = new Set([
    'connect',
    'message',
    'inmail',
    'email',
    'reminder',
    'sift',
    'voice_note',
    'video_note',
  ]);
  const addingStepType =
    editingStep?.type === 'add' &&
    editingStep.afterStepId === lastLeafId &&
    editingStep.intent !== 'new-day' &&
    editingStep.data != null &&
    ADD_TYPE_INLINE.has(editingStep.data.type)
      ? (editingStep.data.type as CadenceStepTypePure)
      : undefined;

  return (
    <>
      <CampaignDayCardPure
        date={new Date(msUpToNow)}
        now={undefined}
        steps={steps}
        // Smaller option buttons so the picker fits inside the
        // narrower pink "if not accepted" well without wrapping.
        pickerOptionSize="sm"
        // No date pill in this synthetic card — the wait_for_connect
        // card above already explains the timing ("not accepted after
        // N days"). Treat it as not-different from template; the
        // per-row tint still surfaces individual diffs.
        isDifferentFromTemplate={false}
        templateHref={templateHref}
        isPickingStepType={isPickingHere}
        addingStepType={addingStepType}
        disabledStepReasons={
          alreadyConnectedToContact
            ? { connect: 'You are already connected to this contact.' }
            : undefined
        }
        editingStepId={
          editingStep?.type === 'edit' && leafIds.includes(editingStep.stepId)
            ? editingStep.stepId
            : undefined
        }
        newStepPreviousEmailSubject={findCadenceThreadSubjectForNewStep(
          nodes,
          { afterStepId: lastLeafId },
          cadenceEmailThread,
        )}
        voiceCallingEnabled={voiceCallingEnabled}
        renderCadenceCallButton={renderCadenceCallButton}
        onMarkCallStepDone={onMarkCallStepDone}
        readOnly={readOnly}
        variables={variables}
        documents={documents}
        renderSiftConditionBuilder={renderSiftConditionBuilder ?? (() => null)}
        reminderActions={reminderActions}
        reminderActionsLoading={reminderActionsLoading}
        onCreateReminder={onCreateReminder ?? (async () => {})}
        reminderCreating={reminderCreating}
        onReminderError={onReminderError ?? (() => {})}
        onEditStep={(stepId) => setEditingStep({ type: 'edit', option: 'one', stepId })}
        onDeleteStep={onDeleteStep}
        onResolveStep={resolveStep}
        stepHistoryOptions={stepHistoryOptions}
        onSaveStep={async (stepId, data) => {
          const update = updateStepDataFromEditor(data);
          await onUpdateStep(stepId, update);
          setEditingStep(undefined);
        }}
        onCancelEditStep={() => setEditingStep(undefined)}
        onAddStep={() =>
          setEditingStep({ type: 'add', option: 'one', afterStepId: lastLeafId })
        }
        onPickStepType={(type) => {
          if (type === 'follow' || type === 'likeRecentPosts' || type === 'visit') {
            void onCreateStep({
              type: type as StepType,
              delay: 0,
              afterStepId: lastLeafId,
              ...(type === 'likeRecentPosts' && { postsToLike: 1 }),
            });
            setEditingStep(undefined);
            return;
          }
          setEditingStep({
            type: 'add',
            option: 'one',
            afterStepId: lastLeafId,
            data: { id: 'pending', type: type as StepType, delay: 0 },
          });
        }}
        onCancelPickStepType={() => setEditingStep(undefined)}
        onSaveNewStep={async (type, data: StepData) => {
          const create = createStepDataFromEditor(type as StepType, data, {
            delay: 0,
            afterStepId: lastLeafId,
          });
          await onCreateStep(create);
          setEditingStep(undefined);
        }}
        onCancelAddStep={() =>
          setEditingStep({ type: 'add', option: 'one', afterStepId: lastLeafId })
        }
      />
      {tailId != null && renderCGStep({ nodeId: tailId, msUpToNow })}
    </>
  );
}

/**
 * Add-step affordance for an empty branch (Yes or No) of a
 * `wait_for_connect` step. The `branchOption` prop discriminates which
 * branch the new step belongs to:
 *
 *   - `'one'` → Yes (accepted) branch — the main timeline continuation
 *   - `'two'` → No (not accepted) branch — the conditional path
 *
 * The option is passed both to `onCreateStep` (so the server attaches
 * the new node to the right branch) AND into `editingStep` (so the
 * picker / inline editor state is scoped per branch).
 */
function BranchAdd({
  afterStepId,
  previousEmailSubject,
  branchOption,
  defaultDelayDays = 2,
  pickerOptionSize,
  editingStep,
  setEditingStep,
  variables,
  documents,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  alreadyConnectedToContact,
  voiceCallingEnabled,
  onCreateStep,
}: {
  afterStepId: string;
  /** Subject the new step's email would continue (see `findCadenceThreadSubjectForNewStep`). */
  previousEmailSubject: string | null;
  branchOption: 'one' | 'two';
  /** Forwarded to the underlying picker — see CampaignStepPickerPure. */
  pickerOptionSize?: 'sm' | 'md';
  /**
   * Default wait length for the new day. Drives BOTH the picker
   * card's "AFTER WAITING N DAYS" caption AND the `delay` value
   * passed to `onCreateStep`. Pass `0` to suppress the caption AND
   * create the step with no leading delay (e.g. for the wait-for-
   * connect No branch — the wait_for_connect step itself owns the
   * delay; the No-branch step fires immediately when the timeout
   * trips, so no extra delay is needed).
   */
  defaultDelayDays?: number;
  editingStep: EditStepPure | undefined;
  setEditingStep: (value: EditStepPure | undefined) => void;
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => React.ReactNode;
  reminderActions?: ReminderAction[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;
  alreadyConnectedToContact?: boolean;
  voiceCallingEnabled?: boolean;
  onCreateStep: (data: CreateStepData) => unknown;
}) {
  const NEW_DAY_DEFAULT_MS = daysToMs(defaultDelayDays);
  // The picker / editor state is keyed by `(afterStepId, option)` so
  // the Yes-empty and No-empty pickers don't share state when both
  // branches happen to be empty on the same wait_for_connect.
  const isHere =
    editingStep?.type === 'add' &&
    editingStep.intent === 'new-day' &&
    editingStep.afterStepId === afterStepId &&
    editingStep.option === branchOption;
  const isPickingHere = isHere && editingStep.data == null;
  const addingHere = isHere && editingStep.data != null
    ? (editingStep.data.type as CadenceStepTypePure)
    : undefined;

  const baseEditingStep: EditStepPure = {
    type: 'add',
    option: branchOption,
    intent: 'new-day',
    afterStepId,
  };

  return (
    <CampaignNewDayPickerPure
      defaultDelayDays={defaultDelayDays}
      previousEmailSubject={previousEmailSubject}
      hasNeighborAbove={false}
      hasNeighborBelow={false}
      pickerOptionSize={pickerOptionSize}
      isPickingStepType={isPickingHere}
      addingStepType={addingHere}
      existingTypes={[]}
      disabledStepReasons={
        alreadyConnectedToContact
          ? { connect: 'You are already connected to this contact.' }
          : undefined
      }
      voiceCallingEnabled={voiceCallingEnabled}
      variables={variables}
      documents={documents}
      renderSiftConditionBuilder={renderSiftConditionBuilder}
      reminderActions={reminderActions}
      reminderActionsLoading={reminderActionsLoading}
      onCreateReminder={onCreateReminder}
      reminderCreating={reminderCreating}
      onReminderError={onReminderError}
      onAddStep={() => setEditingStep(baseEditingStep)}
      onCancelPickStepType={() => setEditingStep(undefined)}
      onPickStepType={(type) => {
        if (type === 'follow' || type === 'likeRecentPosts' || type === 'visit') {
          void onCreateStep({
            type: type as StepType,
            delay: NEW_DAY_DEFAULT_MS,
            afterStepId,
            option: branchOption,
            ...(type === 'likeRecentPosts' && { postsToLike: 1 }),
          });
          setEditingStep(undefined);
          return;
        }
        setEditingStep({
          ...baseEditingStep,
          data: { id: 'pending', type: type as StepType, delay: NEW_DAY_DEFAULT_MS },
        });
      }}
      onSaveNewStep={async (type, data: StepData) => {
        const create = createStepDataFromEditor(type as StepType, data, {
          delay: NEW_DAY_DEFAULT_MS,
          afterStepId,
          // Critical: route to the correct branch. Without this the
          // server defaults to option='one' (Yes) and the new step
          // lands on the wrong path.
          option: branchOption,
        });
        await onCreateStep(create);
        setEditingStep(undefined);
      }}
      onCancelAddStep={() => setEditingStep(baseEditingStep)}
    />
  );
}
